import React, { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabaseAuth } from 'lib/supabaseAuth';
import './Redeem.css';

// Gift codes look like TOAST-7K2M-QX9P. Anything else in ?code= is a sign-in code
// coming back from Google or an email confirmation link.
const GIFT_CODE_PATTERN = /^TOAST[-\s]?[0-9A-Z]{4}[-\s]?[0-9A-Z]{4}$/i;
const STORED_CODE_KEY = 'toastie-gift-code';

type AuthMode = 'sign-in' | 'sign-up';

type RedeemResult =
  | { kind: 'success'; message: string; queued: boolean }
  | { kind: 'error'; error: string; message: string };

// The code is kept for this tab only, just long enough to survive the Google sign-in
// redirect. It's cleared after redeeming or signing out, and never restored on a fresh visit.
const remember = (code: string) => {
  try { sessionStorage.setItem(STORED_CODE_KEY, code); } catch { /* storage blocked: fine */ }
};
const recall = (): string => {
  try { return sessionStorage.getItem(STORED_CODE_KEY) ?? ''; } catch { return ''; }
};
const forget = () => {
  try {
    sessionStorage.removeItem(STORED_CODE_KEY);
    localStorage.removeItem(STORED_CODE_KEY); // clean up from the earlier version
  } catch { /* fine */ }
};

const redirectUrl = () => `${window.location.origin}/redeem`;

export const Redeem: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [session, setSession] = useState<Session | null>(null);
  const [checkingSession, setCheckingSession] = useState(true);

  const [code, setCode] = useState('');
  const [mode, setMode] = useState<AuthMode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authMessage, setAuthMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);

  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState<RedeemResult | null>(null);

  // --- Read the URL once: a gift code, or a sign-in code coming back -------
  useEffect(() => {
    const urlCode = searchParams.get('code');
    const urlError = searchParams.get('error_description');

    const init = async () => {
      if (urlError) {
        setAuthMessage({ tone: 'error', text: urlError });
      }

      if (urlCode && GIFT_CODE_PATTERN.test(urlCode.trim())) {
        // Arrived from a gift email link.
        const gift = urlCode.trim().toUpperCase();
        setCode(gift);
        remember(gift);
      } else if (urlCode || urlError) {
        // Back from Google or an email confirmation link: restore the code they were redeeming.
        const stored = recall();
        setCode(stored);
        if (urlCode) {
          const { error } = await supabaseAuth.auth.exchangeCodeForSession(urlCode);
          if (error) {
            setAuthMessage({
              tone: 'info',
              text: 'Your email is confirmed. Please sign in below to redeem your gift.',
            });
          }
        }
        // Tidy the URL so a refresh doesn't try the sign-in code again.
        setSearchParams(stored ? { code: stored } : {}, { replace: true });
      } else {
        // A plain visit to /redeem: start empty.
        forget();
      }

      const { data } = await supabaseAuth.auth.getSession();
      setSession(data.session);
      setCheckingSession(false);
    };

    init();
    const { data: sub } = supabaseAuth.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
    // Runs once on load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCode = (value: string) => {
    const upper = value.toUpperCase();
    setCode(upper);
    remember(upper);
    setResult(null);
  };

  // --- Sign in / sign up ----------------------------------------------------
  const handleEmailAuth = async (e: FormEvent) => {
    e.preventDefault();
    setAuthBusy(true);
    setAuthMessage(null);

    if (mode === 'sign-in') {
      const { error } = await supabaseAuth.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthMessage({
          tone: 'error',
          text: error.message === 'Invalid login credentials'
            ? "That email and password don't match a Toastie account. Use the same details you use in the app."
            : error.message,
        });
      }
    } else {
      const { data, error } = await supabaseAuth.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectUrl() },
      });
      if (error) {
        setAuthMessage({ tone: 'error', text: error.message });
      } else if (!data.session) {
        setAuthMessage({
          tone: 'info',
          text: `We've sent a confirmation link to ${email}. Open it on this device and you'll come straight back here to redeem.`,
        });
      }
    }
    setAuthBusy(false);
  };

  const handleGoogle = async () => {
    setAuthMessage(null);
    remember(code);
    const { error } = await supabaseAuth.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl() },
    });
    if (error) setAuthMessage({ tone: 'error', text: error.message });
  };

  const handleSignOut = async () => {
    await supabaseAuth.auth.signOut();
    forget();
    setCode('');
    setResult(null);
    setSearchParams({}, { replace: true });
  };

  // --- Redeem -----------------------------------------------------------------
  const handleRedeem = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    setResult(null);

    const { data, error } = await supabaseAuth.functions.invoke('redeem-gift', { body: { code } });

    if (!error && data?.ok) {
      setResult({ kind: 'success', message: data.message, queued: Boolean(data.queued) });
      forget();
      setSearchParams({}, { replace: true });
    } else {
      let body: { error?: string; message?: string } = {};
      if (error instanceof FunctionsHttpError) {
        body = await error.context.json().catch(() => ({}));
      }
      setResult({
        kind: 'error',
        error: body.error ?? 'network',
        message: body.message ?? "We couldn't reach Toastie just now. Please check your connection and try again.",
      });
      if (body.error === 'not_signed_in') await supabaseAuth.auth.signOut();
    }
    setRedeeming(false);
  }, [code, setSearchParams]);

  // --- Render -------------------------------------------------------------------
  const signedInEmail = session?.user.email;

  return (
    <div className='redeem-page'>
      <Helmet>
        <title>Redeem your gift — Toastie Health</title>
        <meta name='robots' content='noindex' />
      </Helmet>

      <div className='redeem-card'>
        <p className='redeem-eyebrow'>A gift for you 💖</p>
        <h1 className='redeem-title'>Redeem your Toastie Pro gift</h1>

        {result?.kind === 'success' ? (
          <div className='redeem-success'>
            <p className='redeem-success-icon' aria-hidden>🎉</p>
            <p className='redeem-text'>{result.message}</p>
            <p className='redeem-text redeem-muted'>
              Signed in as <strong>{signedInEmail}</strong>. Use this same account in the app.
            </p>
            <a className='button redeem-button-link' href='/download.html'>Get the Toastie app</a>
          </div>
        ) : (
          <>
            <ol className='redeem-steps'>
              <li className={session ? 'done' : 'current'}>Sign in with your Toastie account</li>
              <li className={session ? 'current' : ''}>Enter your gift code</li>
              <li>Open up the app. Pro is unlocked!</li>
            </ol>

            {checkingSession ? (
              <p className='redeem-text redeem-muted'>Loading…</p>
            ) : !session ? (
              <section className='redeem-auth'>
                <p className='redeem-text'>
                  Sign in with the account you use in the Toastie app, or create one if you're new.
                  Your gift goes onto this account.
                </p>

                <button type='button' className='redeem-google' onClick={handleGoogle}>
                  <svg width='18' height='18' viewBox='0 0 48 48' aria-hidden>
                    <path fill='#FFC107' d='M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z' />
                    <path fill='#FF3D00' d='M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z' />
                    <path fill='#4CAF50' d='M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z' />
                    <path fill='#1976D2' d='M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z' />
                  </svg>
                  Continue with Google
                </button>

                <div className='redeem-divider'><span>or</span></div>

                <form onSubmit={handleEmailAuth} className='redeem-form'>
                  <div className='label-input-pair'>
                    <label htmlFor='redeem-email'>Email</label>
                    <input
                      id='redeem-email'
                      className='rounded-input-box'
                      type='email'
                      autoComplete='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className='label-input-pair'>
                    <label htmlFor='redeem-password'>Password</label>
                    <input
                      id='redeem-password'
                      className='rounded-input-box'
                      type='password'
                      autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  <button className='button redeem-submit' type='submit' disabled={authBusy}>
                    {authBusy ? 'Please wait…' : mode === 'sign-in' ? 'Sign in' : 'Create account'}
                  </button>
                </form>

                {authMessage && (
                  <p className={`redeem-notice ${authMessage.tone}`} role='status'>{authMessage.text}</p>
                )}

                <p className='redeem-text redeem-muted redeem-switch'>
                  {mode === 'sign-in' ? (
                    <>New to Toastie?{' '}
                      <button type='button' className='redeem-link' onClick={() => { setMode('sign-up'); setAuthMessage(null); }}>
                        Create an account
                      </button>
                    </>
                  ) : (
                    <>Already have an account?{' '}
                      <button type='button' className='redeem-link' onClick={() => { setMode('sign-in'); setAuthMessage(null); }}>
                        Sign in
                      </button>
                    </>
                  )}
                </p>
              </section>
            ) : (
              <section className='redeem-code'>
                <p className='redeem-signed-in'>
                  Signed in as <strong>{signedInEmail}</strong>{' '}
                  <button type='button' className='redeem-link' onClick={handleSignOut}>Not you?</button>
                </p>

                <form onSubmit={handleRedeem} className='redeem-form'>
                  <div className='label-input-pair'>
                    <label htmlFor='redeem-code'>Gift code</label>
                    <input
                      id='redeem-code'
                      className='rounded-input-box redeem-code-input'
                      type='text'
                      inputMode='text'
                      autoCapitalize='characters'
                      autoComplete='off'
                      spellCheck={false}
                      placeholder='TOAST-XXXX-XXXX'
                      value={code}
                      onChange={(e) => updateCode(e.target.value)}
                      required
                    />
                  </div>
                  <button className='button redeem-submit' type='submit' disabled={redeeming || !code.trim()}>
                    {redeeming ? 'Redeeming…' : 'Redeem'}
                  </button>
                </form>

                {result?.kind === 'error' && (
                  <div className='redeem-notice error' role='alert'>
                    <p>{result.message}</p>
                    {result.error === 'store_subscription_renewing' && (
                      <div className='redeem-help'>
                        <p><strong>iPhone:</strong> Settings → your name → Subscriptions → Toastie → Cancel Subscription</p>
                        <p><strong>Android:</strong> Play Store → your profile → Payments &amp; subscriptions → Subscriptions → Toastie → Cancel</p>
                        <p>It can take a few minutes to update. Then come back and tap Redeem again.</p>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>

      <p className='redeem-footer'>
        Questions or running into issues? Email <a href='mailto:hello@toastie.au'>hello@toastie.au</a>
      </p>
    </div>
  );
};
